export default class Client {
	static ABORT_MESSAGE = 'Same request is coming';
	companySearchAbortController: null | AbortController = null;
	locationSearchAbortController: null | AbortController = null;

	#apiDomain: string | null;
	#apiUserData: string | null;

	constructor() {
		this.#apiDomain = this.#getDomain();
		this.#apiUserData = this.#getApiUserData();
	}

	#getDomain(): string | null {
		let heads = document.getElementsByTagName('head');
		if (heads.length === 1 && heads[0] instanceof Element && heads[0].dataset instanceof DOMStringMap) {
			let val = heads[0].dataset.apidomain;
			return val === undefined ? '' : val;
		}
		console.error('Can not get apiDomain');
		return null;
	}

	#getApiUserData(): string | null {
		let heads = document.getElementsByTagName('head');
		if (heads.length === 1 && heads[0] instanceof Element && heads[0].dataset instanceof DOMStringMap) {
			let val = heads[0].dataset.apiuserdata;
			return val === undefined ? null : val;
		}
		console.error('Can not get apiUserData');
		return null;
	}

	abortRequest(controller: null | AbortController) {
		if (controller !== null) {
			controller.abort(Client.ABORT_MESSAGE);
		}
	}

	async request(url: string, method: string, params?: any) {
		const apiData = this.#apiUserData;
		const config: RequestInit = {
			method,
			signal: params?.signal,
			headers: {
				...(typeof apiData === 'string' && apiData.trim().length > 0 ? { UserData: apiData } : {})
			}
		};

		if (method !== 'GET' && method !== 'HEAD' && params !== undefined) {
			config.body = JSON.stringify(params);
		}
		const domain = this.#apiDomain;
		if (typeof domain !== 'string' || domain.trim().length < 0) {
			throw new Error('No domain for api');
		}
		const response = await fetch(domain + url, config);

		if (!response.ok) {
			let body: any = null;
			try {
				body = await response.json();
			} catch {
				// body nie je JSON — necháme null
			}
			const error: any = new Error(`Response: ${response.status}`);
			error.status = response.status;
			error.response = body;
			throw error;
		}

		if (params?.signal && params.signal.aborted) {
			return null;
		}

		return await response.json();
	}

	async get(url: string, params: object) {
		return await this.request(`${url}?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`, 'GET');
	}

	async post(url: string, params: object) {
		try {
			return await this.request(url, 'POST', params);
		} catch (err: unknown) {
			const asAny = err as any;
			// this happens when onKeyUp is fired - with every character it cancels previous request and runs new one - this mutes errors in javascript console
			if (
				(typeof err === 'string' && err.includes(Client.ABORT_MESSAGE)) ||
				(asAny &&
					(asAny.name === 'AbortError' ||
						(typeof asAny.message === 'string' && asAny.message.includes(Client.ABORT_MESSAGE))))
			) {
				return null;
			}
			throw err;
		}
	}

	async put(url: string, params: object) {
		return await this.request(url, 'PUT', params);
	}

	async delete(url: string, params: object) {
		return await this.request(url, 'DELETE', params);
	}

	// specific calls

	async companySearch(
		search: string,
		limit: number = 10,
		onlyUniqueName: boolean = false,
		onlyWithOffer: boolean = false
	) {
		this.abortRequest(this.companySearchAbortController);
		this.companySearchAbortController = new AbortController();

		return this.post('/v1/front/review/company/search', {
			search,
			limit,
			onlyUniqueName,
			onlyWithOffer,
			signal: this.companySearchAbortController?.signal
		});
	}

	async locationsSearch(search: string) {
		this.abortRequest(this.locationSearchAbortController);
		this.locationSearchAbortController = new AbortController();

		try {
			const response = await fetch('/data-locations.json?q=' + search, {
				signal: this.locationSearchAbortController?.signal
			});

			if (!response.ok) {
				throw new Error(`Response: ${response.status}`);
			}

			if (this.locationSearchAbortController?.signal && this.locationSearchAbortController.signal.aborted) {
				throw new Error('Response: request aborted');
			}

			return await response.json();
		} catch (err: unknown) {
			const asAny = err as any;
			// this happens when onKeyUp is fired - with every character it cancels previous request and runs new one - this mutes errors in javascript console
			if (
				(typeof err === 'string' && err.includes(Client.ABORT_MESSAGE)) ||
				(asAny &&
					(asAny.name === 'AbortError' ||
						(typeof asAny.message === 'string' && asAny.message.includes(Client.ABORT_MESSAGE))))
			) {
				return null;
			}

			throw err;
		}
	}

	async benefitList() {
		return await this.request('/v1/public/offer/benefit-subject', 'GET');
	}

	async jobSubjectList() {
		return await this.request('/v1/public/offer/job/subject', 'GET');
	}

	async companySuggest(search: string, limit: number = 10) {
		return this.post('/v1/front/review/company/suggest', { search, limit });
	}

	async jobSubjectProfessionList() {
		return await this.request('/v1/public/offer/job/subject-profession', 'GET');
	}

	async brigadeSubjectList() {
		return await this.request('/v1/public/offer/brigade/subject', 'GET');
	}

	async departmentList(slug: string) {
		return await this.request('/v1/front/review/company/department/' + slug, 'GET');
	}

	async ratingDataList() {
		return await this.request('/v1/front/review/rating/list', 'GET');
	}

	async validationEmail(data: any) {
		return this.post('/v1/front/review/validation/email', data);
	}

	async addReview(data: any) {
		return this.post('/v1/front/review/review/create', data);
	}

	async addReviewReport(data: any) {
		return this.post('/v1/front/review/report/create', data);
	}

	async addOfferReport(data: any) {
		return this.post('/v1/front/offer/report/create', data);
	}

	async getInterviewOptions() {
		return await this.request('/v1/front/review/interview/options', 'GET');
	}

	async getInterviewRatingDataList() {
		return await this.request('/v1/front/review/interview/rating/list', 'GET');
	}

	async addInterviewReview(data: any) {
		return this.post('/v1/front/review/interview/create', data);
	}

	async addInterviewReport(data: any) {
		return this.post('/v1/front/review/interview/report/create', data);
	}
}
